import { useQuery } from "@apollo/client";
import { omit } from "lodash";
import React, { FunctionComponent } from "react";
import { StyleSheet, ScrollView, ActivityIndicator } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useDispatch, useSelector } from "react-redux";

import Button from "../../components/Button";
import Header from "../../components/Header";
import Text from "../../components/Text";

import { RootState } from "../../state";
import {
  bootstrap,
  ConsentFlowState,
  SelectedService,
} from "../../state/consentFlow";
import { colors } from "../../theme";
import { Customer, VisitService } from "../../types";
import { FETCH_CUSTOMER } from "../ExistingCustomerScreen/gql";
import EditableSection from "../../components/EditableSection";
import CustomerRow from "../ExistingCustomerScreen/CustomerRow";
import useCustomer from "../../hooks/useCustomer";
import { formatDateOfBirth } from "../../utils/customer";
import Container from "../../components/Container";
import StickyFooter from "../../components/StickyFooter";
import { useFirebaseAuth } from "../../firebase/AuthProvider";

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.black,
    flex: 1,
  },
  scrollView: {
    paddingHorizontal: 20,
  },
  sectionText: {
    color: colors.midGrey,
  },
  sectionTextError: {
    color: colors.statusRed,
  },
});

function existingServicesBootstrap(
  services: VisitService[],
): SelectedService[] {
  return services.map((visitService) => ({
    isExistingService: true,
    id: visitService.id,
    serviceId: visitService.service.id,
    serviceType: visitService.service.serviceType?.type,
    submittedInformation: visitService.submittedInformation?.map((si) => ({
      name: si.name,
      label: si.label,
      value: si.value || "",
    })),
  }));
}

function determineNextScreen(
  customer: Customer,
  consentState: ConsentFlowState,
) {
  const hasProfileInformation =
    customer && customer.firstName && customer.address;

  if (!hasProfileInformation) {
    return "IdVerification";
  }

  if (Object.keys(consentState.services).length === 0) {
    return "NewServiceForVisit";
  }

  // Always take the customer to medical history screen
  return "MedicalHistoryForVisit";
}

interface Props {
  route: any;
  navigation: any;
}

const ExistingVisitScreen: FunctionComponent<Props> = ({
  route,
  navigation,
}) => {
  const { visit } = route.params;
  const dispatch = useDispatch();
  const { currentCustomer } = useFirebaseAuth();
  const consentState = useSelector((state: RootState) => state.consentFlow);
  const subject = useCustomer(
    consentState.subject.id || (currentCustomer?.id as string),
  );

  const visitServices = visit.services || [];

  const { loading } = useQuery(FETCH_CUSTOMER, {
    variables: { customerId: subject?.id },
    onCompleted: ({ customer }) => {
      dispatch(
        bootstrap({
          services: existingServicesBootstrap(visitServices),
          medicalHistory: customer?.medicalHistory?.medicalConditions || [],
          medicalConcerns: {
            answers:
              omit(customer?.medicalHistory, [
                "id",
                "additionalNotes",
                "medicalConditions",
                "__typename",
              ]) || [],
            additionalNotes: customer?.medicalHistory?.additionalNotes,
          },
        }),
      );
    },
  });

  return (
    <SafeAreaView style={styles.container} edges={["right", "bottom", "left"]}>
      <Container>
        <ScrollView style={styles.scrollView}>
          <Header
            title="Your Visit"
            description="Please complete any remaining sections and check to make sure we didn’t miss anything."
          />

          {loading && <ActivityIndicator color={colors.yellow} />}

          {!loading && (
            <>
              {consentState.subject.role !== "self" && (
                <EditableSection
                  label="Minor Info"
                  isDisabled
                  onEditPress={() => {}}
                >
                  <Text style={styles.sectionText}>
                    {subject?.firstName} {subject?.lastName}
                  </Text>
                  <Text style={styles.sectionText}>
                    DOB: {formatDateOfBirth(subject?.dateOfBirth)}
                  </Text>
                </EditableSection>
              )}

              <EditableSection
                label="Basic Info"
                onEditPress={() => {
                  navigation.navigate("IdVerification");
                }}
              >
                <CustomerRow customer={currentCustomer as Customer} />
              </EditableSection>

              <EditableSection
                label="Procedure Details"
                onEditPress={() => {
                  navigation.navigate("ReviewServicesForVisit");
                }}
              >
                {visitServices.map((visitService: VisitService) => {
                  return (
                    <Text
                      key={`service-${visitService.id}`}
                      style={styles.sectionText}
                    >
                      {visitService.service.serviceType.name} -{" "}
                      {visitService.service.name}
                    </Text>
                  );
                })}

                {visitServices.length === 0 && (
                  <Text style={styles.sectionTextError}>
                    No procedure selected
                  </Text>
                )}
              </EditableSection>
              <EditableSection
                label="Health Info"
                onEditPress={() => {
                  navigation.navigate("MedicalHistoryForVisit");
                }}
              >
                {consentState.medicalHistory.length > 0 &&
                  consentState.medicalHistory.map((item, idx) => (
                    <Text key={idx} style={styles.sectionText}>
                      {item}
                    </Text>
                  ))}
                {Object.keys(consentState.medicalConcerns.answers).length ===
                0 ? (
                  <Text style={styles.sectionTextError}>
                    No medical history on file
                  </Text>
                ) : (
                  <Text style={styles.sectionText}>
                    No medical history of note
                  </Text>
                )}
              </EditableSection>
            </>
          )}
        </ScrollView>
      </Container>
      <StickyFooter>
        <Button
          title="Next"
          isLoading={loading}
          onPress={() => {
            const nextScreen = determineNextScreen(
              currentCustomer as Customer,
              consentState,
            );

            navigation.navigate(nextScreen);
          }}
          disabled={loading}
        />
      </StickyFooter>
    </SafeAreaView>
  );
};

export default ExistingVisitScreen;
